import {
  Card,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Button,
  Spin,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { API } from "../../config/Api";
import { GET, POST } from "../../utils/apiRequest";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const ProfitLoss = () => {
  const user = useSelector((state: any) => state?.User?.user);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState<string>("today");
  const [customDateRange, setCustomDateRange] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());

  // Financial data
  const [subscriptionIncome, setSubscriptionIncome] = useState(0);
  const [otherIncome, setOtherIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [profit, setProfit] = useState(0);

  useEffect(() => {
    if (user?.office?.id) {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const day = today.getDate();

      const startOfDay = new Date(year, month, day, 0, 0, 0, 0);
      const endOfDay = new Date(year, month, day, 23, 59, 59, 999);

      setStartDate(startOfDay);
      setEndDate(endOfDay);
      fetchFinancialData({
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString(),
      });
    }
  }, [user?.office?.id]);

  const fetchFinancialData = async (dateFilterObj: any) => {
    try {
      setLoading(true);

      console.log("📅 Request Date Range:", dateFilterObj);

      // Call the profit-loss API
      const requestBody = {
        office_id: user?.office?.id,
        startDate: dateFilterObj.startDate,
        endDate: dateFilterObj.endDate,
      };

      const response: any = await POST(API.GET_PROFIT_LOSS, requestBody);

      console.log("📊 Profit & Loss API Response:", response);

      if (response?.status && response?.data) {
        const data = response.data;

        // Set all the values from API response
        setSubscriptionIncome(Number(data.subscription_income) || 0);
        setOtherIncome(Number(data.other_income) || 0);
        setTotalExpenses(Number(data.total_expenses) || 0);
        setProfit(Number(data.profit_loss) || 0);

        console.log("💰 Final Values:", {
          subscriptionIncome: data.subscription_income,
          otherIncome: data.other_income,
          totalExpenses: data.total_expenses,
          totalIncome: data.total_income,
          profit: data.profit_loss
        });
      }
    } catch (error) {
      console.error("❌ Error fetching financial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeFromFilter = (filter: string) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    let start: Date, end: Date;

    switch (filter) {
      case "today":
        start = new Date(year, month, day, 0, 0, 0, 0);
        end = new Date(year, month, day, 23, 59, 59, 999);
        break;
      case "yesterday":
        start = new Date(year, month, day - 1, 0, 0, 0, 0);
        end = new Date(year, month, day - 1, 23, 59, 59, 999);
        break;
      case "this_month":
        start = new Date(year, month, 1, 0, 0, 0, 0);
        end = new Date(year, month, day, 23, 59, 59, 999);
        break;
      case "last_month":
        start = new Date(year, month - 1, 1, 0, 0, 0, 0);
        end = new Date(year, month, 0, 23, 59, 59, 999);
        break;
      case "last_30_days":
        start = new Date(year, month, day - 30, 0, 0, 0, 0);
        end = new Date(year, month, day, 23, 59, 59, 999);
        break;
      default:
        start = new Date(year, month, day, 0, 0, 0, 0);
        end = new Date(year, month, day, 23, 59, 59, 999);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
    if (value !== "custom") {
      const dateRange = getDateRangeFromFilter(value);
      setStartDate(new Date(dateRange.startDate));
      setEndDate(new Date(dateRange.endDate));
      fetchFinancialData(dateRange);
    }
  };

  const handleCustomDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].toDate();
      const endDate = dates[1].toDate();

      // Set to start of day (00:00:00.000)
      const start = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        0, 0, 0, 0
      );

      // Set to end of day (23:59:59.999)
      const end = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        23, 59, 59, 999
      );

      setCustomDateRange(dates);
      setStartDate(start);
      setEndDate(end);
      const dateRange = {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
      fetchFinancialData(dateRange);
    }
  };

  const handleRefresh = () => {
    const dateRange =
      dateFilter === "custom" && customDateRange
        ? {
            startDate: customDateRange[0].toDate().toISOString(),
            endDate: customDateRange[1].toDate().toISOString(),
          }
        : getDateRangeFromFilter(dateFilter);
    fetchFinancialData(dateRange);
  };

  const totalIncome = subscriptionIncome + otherIncome;

  return (
    <div className="screenBox">
      <Spin spinning={loading}>
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
              paddingBottom: "16px",
              borderBottom: "2px solid #f0f0f0",
            }}
          >
            <h5
              className="user-heading"
              style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}
            >
              Profit & Loss Statement
            </h5>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              size="large"
            >
              Refresh
            </Button>
          </div>

          {/* Filter Section */}
          <Card
            size="small"
            style={{
              marginBottom: "20px",
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "8px",
            }}
          >
            <Row gutter={16}>
              <Col flex="250px">
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Date Range"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  size="large"
                >
                  <Select.Option value="today">Today</Select.Option>
                  <Select.Option value="yesterday">Yesterday</Select.Option>
                  <Select.Option value="this_month">This Month</Select.Option>
                  <Select.Option value="last_month">Last Month</Select.Option>
                  <Select.Option value="last_30_days">
                    Last 30 Days
                  </Select.Option>
                  <Select.Option value="custom">Custom Date</Select.Option>
                </Select>
              </Col>
              {dateFilter === "custom" && (
                <Col flex="350px">
                  <RangePicker
                    style={{ width: "100%" }}
                    value={customDateRange}
                    onChange={handleCustomDateChange}
                    size="large"
                    placeholder={["Start Date", "End Date"]}
                  />
                </Col>
              )}
            </Row>
          </Card>

          {/* Summary Cards Grid */}
          <Row gutter={16} style={{ marginBottom: "20px" }}>
            <Col xs={24} md={12}>
              <Card
                bordered
                style={{
                  backgroundColor: "#f6ffed",
                  borderColor: "#b7eb8f",
                }}
              >
                <Statistic
                  title="Total Income"
                  value={totalIncome}
                  precision={2}
                  valueStyle={{ color: "#3f8600" }}
                  prefix="₹"
                  suffix={<ArrowDownOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card
                bordered
                style={{
                  backgroundColor: "#fff1f0",
                  borderColor: "#ffccc7",
                }}
              >
                <Statistic
                  title="Total Expenses"
                  value={totalExpenses}
                  precision={2}
                  valueStyle={{ color: "#cf1322" }}
                  prefix="₹"
                  suffix={<ArrowUpOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Net Profit/Loss Card */}
          <Card
            bordered
            style={{
              marginBottom: "20px",
              backgroundColor: profit >= 0 ? "#f6ffed" : "#fff1f0",
              borderColor: profit >= 0 ? "#52c41a" : "#ff4d4f",
              borderWidth: "2px",
            }}
          >
            <Statistic
              title={profit >= 0 ? "Net Profit" : "Net Loss"}
              value={Math.abs(profit)}
              precision={2}
              valueStyle={{
                color: profit >= 0 ? "#3f8600" : "#cf1322",
                fontSize: "32px",
                fontWeight: "bold",
              }}
              prefix="₹"
              suffix={<DollarOutlined />}
            />
          </Card>

          {/* Breakdown Section */}
          <Row gutter={16}>
            <Col xs={24}>
              <Card
                title="Income Breakdown"
                bordered
                style={{ marginBottom: "16px" }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Statistic
                      title="Subscription Income"
                      value={subscriptionIncome}
                      precision={2}
                      prefix="₹"
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Statistic
                      title="Other Income"
                      value={otherIncome}
                      precision={2}
                      prefix="₹"
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Card>
      </Spin>
    </div>
  );
};

export default ProfitLoss;
